import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

interface UserData {
  user_id: string;
  email: string;
  name?: string;
  created_at?: number; // Unix timestamp
  user_role?: 'older_adult' | 'family_member' | 'caregiver' | 'admin';
  thrive_score?: number;
  subscription_plan?: string;
  [key: string]: any;
}

interface IntercomContextType {
  isInitialized: boolean;
  identifyUser: (userData: UserData) => void;
  updateUser: (attributes: Record<string, any>) => void;
  trackEvent: (eventName: string, metadata?: Record<string, any>) => void;
  showMessages: () => void;
  hideMessages: () => void;
  shutdown: () => void;
}

const IntercomContext = createContext<IntercomContextType | undefined>(undefined);

export const useIntercom = () => {
  const context = useContext(IntercomContext);
  if (!context) {
    throw new Error('useIntercom must be used within IntercomProvider');
  }
  return context;
};

interface IntercomProviderProps {
  children: React.ReactNode;
}

export const IntercomProvider: React.FC<IntercomProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const APP_ID = 'do4mdo4q';

  useEffect(() => {
    // Check if Intercom script already exists to prevent duplicate loading
    const existingScript = document.querySelector(`script[src*="widget.intercom.io"]`);
    if (existingScript && window.Intercom) {
      // Intercom already loaded, just initialize
      try {
        window.Intercom('boot', {
          app_id: APP_ID,
        });
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize existing Intercom:', error);
      }
      return;
    }

    // Load Intercom script dynamically to avoid React conflicts
    const script = document.createElement('script');
    script.innerHTML = `
      (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/${APP_ID}';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
    `;
    script.setAttribute('data-intercom-init', 'true');
    document.head.appendChild(script);

    // Initialize after script loads
    const initTimeout = setTimeout(() => {
      try {
        if (window.Intercom) {
          window.Intercom('boot', {
            app_id: APP_ID,
          });
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('Failed to initialize Intercom:', error);
      }
    }, 1000);

    // Cleanup - only clear timeout, don't shutdown Intercom
    return () => {
      clearTimeout(initTimeout);
    };
  }, []);

  const identifyUser = useCallback((userData: UserData) => {
    if (!isInitialized) return;
    
    try {
      if (window.Intercom) {
        window.Intercom('update', {
          user_id: userData.user_id,
          email: userData.email,
          name: userData.name,
          created_at: userData.created_at,
          user_role: userData.user_role,
          thrive_score: userData.thrive_score,
          subscription_plan: userData.subscription_plan,
          ...userData,
        });
      }
    } catch (error) {
      console.error('Failed to identify user in Intercom:', error);
    }
  }, [isInitialized]);

  const updateUser = useCallback((attributes: Record<string, any>) => {
    if (!isInitialized) return;
    
    try {
      if (window.Intercom) {
        window.Intercom('update', attributes);
      }
    } catch (error) {
      console.error('Failed to update user in Intercom:', error);
    }
  }, [isInitialized]);

  const trackEvent = useCallback((eventName: string, metadata?: Record<string, any>) => {
    if (!isInitialized) return;
    
    try {
      if (window.Intercom) {
        window.Intercom('trackEvent', eventName, metadata);
      }
    } catch (error) {
      console.error('Failed to track event in Intercom:', error);
    }
  }, [isInitialized]);

  const showMessages = useCallback(() => {
    if (!isInitialized) return;
    
    try {
      if (window.Intercom) {
        window.Intercom('show');
      }
    } catch (error) {
      console.error('Failed to show Intercom:', error);
    }
  }, [isInitialized]);

  const hideMessages = useCallback(() => {
    if (!isInitialized) return;
    
    try {
      if (window.Intercom) {
        window.Intercom('hide');
      }
    } catch (error) {
      console.error('Failed to hide Intercom:', error);
    }
  }, [isInitialized]);

  const shutdown = useCallback(() => {
    if (!isInitialized) return;
    
    try {
      if (window.Intercom) {
        window.Intercom('shutdown');
      }
      // Reinitialize as anonymous after shutdown
      setTimeout(() => {
        if (window.Intercom) {
          window.Intercom('boot', {
            app_id: APP_ID,
          });
        }
      }, 100);
    } catch (error) {
      console.error('Failed to shutdown Intercom:', error);
    }
  }, [isInitialized, APP_ID]);

  const value: IntercomContextType = {
    isInitialized,
    identifyUser,
    updateUser,
    trackEvent,
    showMessages,
    hideMessages,
    shutdown,
  };

  return (
    <IntercomContext.Provider value={value}>
      {children}
    </IntercomContext.Provider>
  );
};
